import * as yup from "yup";
import { Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "../../../../components/ui/button";
import Common from "../../../../helper/common";
import { FormikInput } from "../../../../components/formik-components";
import { respEnum } from "../../../../constants/appConstant";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../../components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { Checkbox } from "../../../../components/ui/checkbox";

const FormikInitialValues = {
  name: "",
  sortOrder: "",
  isactive: true,
};

const formikValidation = yup.object({
  name: yup
    .string()
    .trim()
    .required("Category Name is required")
    .max(100, "Category name cannot exceed 100 characters"),
  sortOrder: yup
    .number()
    .integer("Sort order must be a whole number")
    .min(0, "Sort order cannot be negative")
    .nullable()
    .max(9999, "Sort order cannot exceed 9,999"),
});

const AddCategory = ({ id, onClose, open, setOpen }) => {
  const formikRef = useRef();
  const dialogRef = useRef();
  const navigate = useNavigate();
  const [isFormikInitialized, setIsFormikInitialized] = useState(false);

  useEffect(() => {
    setIsFormikInitialized(open);
  }, [open]);

  const onSubmitData = async (d, action) => {
    let data = {
      ...d,
      id: id || 0,
      parentid: null, // Only parent categories from UI
      sortOrder: d.sortOrder || null,
    };

    let response = await Common.ApiService.getInstance().request(
      "AddUpdateProductCategory",
      data,
      "POST"
    );

    action.setSubmitting(false);

    if (response.status === respEnum.Success) {
      //Common.SweetAlert.alert(response.message, "success");
      Common.AntDesignMessage.success(response.message);
      onClose(response.data);
      setOpen(false);
      if (!onClose) {
        navigate("/app/products/categories", { replace: true });
      }
    } else {
      Common.SweetAlert.alert(response.message || "An error occurred", "error");
    }
  };

  const getDataInfo = async () => {
    let response = await Common.ApiService.getInstance().request(
      `GetProductCategoryById?id=${id}`
    );
    if (response.status === respEnum.Success && response.data) {
      const categoryData = {
        ...response.data,
        sortOrder: response.data.sortOrder || "",
      };
      formikRef.current.setValues(categoryData);
    }
  };

  useEffect(() => {
    if (id && isFormikInitialized) {
      getDataInfo();
    }
  }, [id, isFormikInitialized]);

  return (
    <div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            size="sm"
            variant="primary"
            className="flex items-center gap-2"
          >
            <Icon icon="heroicons:plus-20-solid" className="h-4 w-4" />
            Add Category
          </Button>
        </DialogTrigger>
        <DialogContent ref={dialogRef} className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-base font-medium flex items-center gap-2">
              <Icon
                icon={!id ? "heroicons:plus-circle" : "heroicons:pencil-square"}
                className="h-5 w-5 text-blue-600"
              />
              {!id ? "Add New" : "Update"} Product Category
            </DialogTitle>
          </DialogHeader>

          <div className="text-sm text-default-500 space-y-4">
            <Formik
              innerRef={formikRef}
              onSubmit={onSubmitData}
              initialValues={FormikInitialValues}
              validationSchema={formikValidation}
              validateOnChange={false}
              validateOnBlur={false}
              enableReinitialize={true}
            >
              {(formikProps) => (
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                      <Icon icon="heroicons:tag" className="h-4 w-4" />
                      Category Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <FormikInput
                          formikProps={formikProps}
                          name={"name"}
                          label={"Name *"}
                          placeholder={"Enter category name"}
                          maxLength="100"
                        />
                      </div>
                      <div>
                        <FormikInput
                          formikProps={formikProps}
                          name={"sortOrder"}
                          label={"Sort Order"}
                          placeholder={"0"}
                          type="number"
                          min="0"
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="isactive"
                          checked={formikProps.values.isactive}
                          onCheckedChange={(value) =>
                            formikProps.setFieldValue("isactive", value)
                          }
                        />
                        <label
                          htmlFor="isactive"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Active
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Formik>
          </div>

          <DialogFooter className="mt-8 gap-2">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                <Icon icon="heroicons:x-mark" className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </DialogClose>

            {isFormikInitialized && formikRef.current && (
              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  formikRef.current?.handleSubmit();
                }}
                disabled={formikRef.current?.isSubmitting}
                className="min-w-[120px]"
              >
                {formikRef.current.isSubmitting ? (
                  <Icon
                    icon="heroicons:arrow-path"
                    className="h-4 w-4 mr-2 animate-spin"
                  />
                ) : (
                  <Icon
                    icon={!id ? "heroicons:plus" : "heroicons:check"}
                    className="h-4 w-4 mr-2"
                  />
                )}
                {formikRef.current.isSubmitting
                  ? "Saving..."
                  : !id
                  ? "Add Category"
                  : "Update Category"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddCategory;
